<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore, type RileyNotification } from '../../store/notificationStore';

const auth   = useAuthStore() as any;
const notif  = useNotificationStore();
const router = useRouter();

onMounted(() => { if (auth.isAuthenticated) notif.startPolling(); });
onUnmounted(() => notif.stopPolling());

// ── grouping (Today / Yesterday / Earlier) ──────────────────────────────────
const bucket = (ts: string): string => {
  const dt = DateTime.fromSQL(ts);
  if (!dt.isValid) return 'Earlier';
  const diff = DateTime.now().startOf('day').diff(dt.startOf('day'), 'days').days;
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return 'Earlier';
};
const groups = computed(() => {
  const by = new Map<string, RileyNotification[]>();
  for (const n of notif.items) {
    const l = bucket(n.timestamp);
    (by.get(l) ?? by.set(l, []).get(l)!).push(n);
  }
  return ['Today', 'Yesterday', 'Earlier']
    .filter((l) => by.has(l))
    .map((l) => ({ label: l, items: by.get(l)! }));
});

// ── helpers ─────────────────────────────────────────────────────────────────
const relTime = (ts: string): string => {
  const dt = DateTime.fromSQL(ts);
  if (!dt.isValid) return '';
  const o = DateTime.now().diff(dt, ['days', 'hours', 'minutes']).toObject();
  const d = Math.floor(o.days ?? 0), h = Math.floor(o.hours ?? 0), m = Math.floor(o.minutes ?? 0);
  if (d >= 7) return dt.toFormat('dd MMM');
  if (d >= 1) return `${d}d`;
  if (h >= 1) return `${h}h`;
  if (m >= 1) return `${m}m`;
  return 'now';
};
const iconFor = (type: string): string =>
  type === 'payment_received' ? 'card'
  : type === 'invoice_issued' ? 'doc'
  : type === 'order_approval_requested' ? 'bell'
  : (type.startsWith('user_') || type === 'role_changed') ? 'user'
  : type === 'notice' ? 'bell'
  : 'box';

const go = (n: RileyNotification) => {
  notif.close();
  if (n.link) router.push(n.link);
};
</script>

<template>
  <transition name="nb-slide">
    <div v-if="notif.expanded" class="nb-panel">
      <div class="nb-panel-head">
        <div class="nb-panel-title">
          Notifications
          <span v-if="notif.unreadCount > 0" class="nb-count">{{ notif.unreadCount }}</span>
        </div>
        <div class="nb-panel-actions">
          <button v-if="notif.unreadCount > 0" class="nb-markall" @click="notif.markAllRead()">Mark all read</button>
          <button class="nb-close" @click="notif.close()" aria-label="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <div class="nb-feed">
        <template v-if="notif.hasAny">
          <template v-for="g in groups" :key="g.label">
            <div class="nb-group">{{ g.label }}</div>
            <button
              v-for="n in g.items"
              :key="n.key"
              class="nb-row"
              :class="{ unread: n.unread }"
              @click="go(n)"
            >
              <span class="nb-ic" :class="'nb-ic--' + n.severity">
                <svg v-if="iconFor(n.type) === 'box'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                <svg v-else-if="iconFor(n.type) === 'doc'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <svg v-else-if="iconFor(n.type) === 'user'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <svg v-else-if="iconFor(n.type) === 'bell'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </span>
              <span class="nb-row-body">
                <span class="nb-row-title">{{ n.title }}</span>
                <span class="nb-row-text">{{ n.body }}</span>
              </span>
              <span class="nb-row-time">{{ relTime(n.timestamp) }}</span>
            </button>
          </template>
        </template>
        <div v-else class="nb-empty">
          <div class="nb-empty-ic">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </div>
          <div class="nb-empty-text">No notifications yet</div>
          <div class="nb-empty-sub">Order, invoice and payment updates will show up here.</div>
        </div>
      </div>
    </div>
  </transition>

  <div v-if="notif.expanded" class="nb-backdrop" @click="notif.close()"></div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

/* ── Dropdown panel (anchored under the bell, top-right) ──────────────────── */
.nb-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 200;
  width: 380px;
  max-width: calc(100vw - 24px);
  font-family: 'DM Sans', sans-serif;
  background: #fff;
  border: 1.5px solid #ccdeff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,61,160,0.14), 0 2px 8px rgba(0,97,235,0.07);
  overflow: hidden;
}

.nb-panel-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: #f0f6ff; border-bottom: 1.5px solid #ccdeff;
}
.nb-panel-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.9rem; font-weight: 700; color: #0f1729;
}
.nb-count {
  background: #0061eb; color: #fff; font-size: 0.66rem; font-weight: 700;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 20px;
  display: inline-flex; align-items: center; justify-content: center;
}
.nb-panel-actions { display: flex; align-items: center; gap: 6px; }
.nb-markall {
  background: none; border: none; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 0.78rem; font-weight: 600; color: #0061eb;
  padding: 4px 8px; border-radius: 6px; transition: background 0.12s;
}
.nb-markall:hover { background: #e3edff; }
.nb-close {
  display: flex; background: none; border: none; cursor: pointer; color: #8a95a6;
  padding: 4px; border-radius: 6px; transition: background 0.12s, color 0.12s;
}
.nb-close:hover { background: #e3edff; color: #0061eb; }

/* ── Feed ─────────────────────────────────── */
.nb-feed { max-height: min(58vh, 460px); overflow-y: auto; }

.nb-group {
  font-size: 0.64rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em;
  padding: 12px 16px 5px; background: #fff;
}

.nb-row {
  display: flex; align-items: flex-start; gap: 12px; width: 100%;
  padding: 11px 16px; background: none; border: none; cursor: pointer;
  text-align: left; border-bottom: 1px solid #f1f6ff;
  position: relative; transition: background 0.12s;
}
.nb-row:hover { background: #f0f6ff; }
.nb-row.unread { background: #f7faff; }
.nb-row.unread::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: #0061eb;
}

.nb-ic {
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.nb-ic--success { background: #dcfce7; color: #166534; }
.nb-ic--info    { background: #dbeafe; color: #1e40af; }
.nb-ic--warning { background: #fef3c7; color: #92400e; }
.nb-ic--error   { background: #ffe4e6; color: #9f1239; }

.nb-row-body { flex: 1; min-width: 0; }
.nb-row-title {
  display: block; font-size: 0.84rem; font-weight: 600; color: #1e293b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.nb-row.unread .nb-row-title { font-weight: 700; color: #0f1729; }
.nb-row-text {
  display: block; font-size: 0.78rem; color: #8a95a6; margin-top: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.nb-row-time {
  flex-shrink: 0; font-size: 0.72rem; color: #b0bcce; font-weight: 500;
  padding-top: 1px; white-space: nowrap;
}

/* ── Empty ────────────────────────────────── */
.nb-empty {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 44px 20px;
}
.nb-empty-ic {
  width: 52px; height: 52px; border-radius: 50%; background: #f0f6ff; color: #6b86b8;
  display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
}
.nb-empty-text { font-size: 0.92rem; font-weight: 600; color: #475569; }
.nb-empty-sub { font-size: 0.78rem; color: #94a3b8; }

/* ── Backdrop ─────────────────────────────── */
.nb-backdrop { position: fixed; inset: 0; z-index: 150; }

/* ── Transition ───────────────────────────── */
.nb-slide-enter-active, .nb-slide-leave-active {
  transition: opacity 0.16s ease, transform 0.2s cubic-bezier(0.16,1,0.3,1);
  transform-origin: top right;
}
.nb-slide-enter-from, .nb-slide-leave-to { opacity: 0; transform: translateY(-8px) scale(0.98); }
</style>
